import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { AlertCircle, CheckCircle2, Flame } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import TrialPaymentInfo from "./TrialPaymentInfo";
import TrialTerms from "./TrialTerms";
import TrialBankAccounts from "./TrialBankAccounts";
import { trialConfig } from "../utils/trialConfig";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { contactInfo } from "../utils/mockData";

export const TrialDemoForm = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [agreedToContact, setAgreedToContact] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      service: "trial-demo-social-media",
      goal: "",
    },
  });

  const selectedService = watch("service");
  const selectedGoal = watch("goal");

  const onSubmit = (data) => {
    if (!agreedToContact) {
      alert("Mohon setujui skema pembayaran & kontak WhatsApp");
      return;
    }

    const message = `
🔥 TRIAL DEMO 1 BULAN – ${trialConfig.brand}

Nama: ${data.fullName}
Bisnis: ${data.businessName}
WhatsApp: ${data.whatsapp}
Email: ${data.email}
Tujuan: ${data.goal}

Saya mendaftar Trial Demo Social Media Management 1 Bulan.

Saya memahami bahwa:
- Trial GRATIS selama ${trialConfig.durationDays} hari
- Tidak ada pembayaran di awal
- Jika melanjutkan ke bulan ke-2, saya membayar Rp ${trialConfig.payAfterTrial.toLocaleString()}
  (50% dari harga normal Rp ${trialConfig.priceNormal.toLocaleString()})

Rekening tujuan:
${trialConfig.bankAccounts.map((b) => `- ${b.bank}: ${b.number} a.n ${b.name}`).join("\n")}

${data.notes ? `\nCatatan:\n${data.notes}` : ""}
`.trim();

    setIsSubmitted(true);

    setTimeout(() => {
      const url = `https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
      setTimeout(() => setIsSubmitted(false), 1000);
    }, 1500);
  };

  if (isSubmitted) {
    return (
      <section
        id="trial-demo-form"
        className="py-20 bg-gray-50 dark:bg-[#121212] scroll-mt-20"
      >
        <div className="container px-4 mx-auto sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white dark:bg-[#0F0F0F] rounded-2xl shadow-xl border-2 border-green-500 p-12">
              <div className="space-y-6 text-center">
                <div className="flex items-center justify-center w-20 h-20 mx-auto bg-green-100 rounded-full dark:bg-green-900/30">
                  <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Terima Kasih! 🎉
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  Formulir Anda berhasil dikirim. Kami akan menghubungi Anda
                  segera via WhatsApp.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Mengalihkan ke WhatsApp...
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="trial-demo-form"
      className="py-20 bg-gray-50 dark:bg-[#121212] scroll-mt-20"
    >
      <div className="container px-4 mx-auto sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Promo Card Container */}
          <div className="bg-white dark:bg-[#0F0F0F] rounded-2xl shadow-xl border-2 border-[#D32F2F] p-8 lg:p-12 relative overflow-hidden">
            {/* Subtle background pattern */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D32F2F]/5 rounded-full blur-3xl -z-0"></div>

            <div className="relative z-10 space-y-8">
              {/* Promo Badge */}
              <div className="text-center">
                <Badge className="bg-[#D32F2F] text-white hover:bg-[#B71C1C] px-6 py-2 text-base">
                  <Flame className="inline w-4 h-4 mr-2" />
                  FORMULIR PENAWARAN SPESIAL
                </Badge>
              </div>

              {/* Title & Subtitle */}
              <div className="space-y-4 text-center">
                <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl dark:text-white">
                  Klaim Trial Demo 1 Bulan Sekarang
                </h2>
                <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                  Isi formulir di bawah ini untuk mendapatkan kesempatan Trial
                  Demo Social Media Management dari Dreams Marketing.
                </p>
                <div className="flex items-center justify-center space-x-2 text-amber-600 dark:text-amber-500">
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    Slot terbatas, diprioritaskan untuk pendaftar lebih awal
                  </span>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label
                    htmlFor="fullName"
                    className="font-medium text-gray-900 dark:text-white"
                  >
                    Nama Lengkap <span className="text-[#D32F2F]">*</span>
                  </Label>
                  <Input
                    id="fullName"
                    {...register("fullName", {
                      required: "Nama lengkap wajib diisi",
                    })}
                    placeholder="Nama lengkap Anda"
                    className="h-12 border-gray-300 bg-gray-50 dark:bg-gray-900 dark:border-gray-700"
                  />
                  {errors.fullName && (
                    <p className="text-sm text-[#D32F2F] flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.fullName.message}
                    </p>
                  )}
                </div>

                {/* Business Name */}
                <div className="space-y-2">
                  <Label
                    htmlFor="businessName"
                    className="font-medium text-gray-900 dark:text-white"
                  >
                    Nama Bisnis / Brand{" "}
                    <span className="text-[#D32F2F]">*</span>
                  </Label>
                  <Input
                    id="businessName"
                    {...register("businessName", {
                      required: "Nama bisnis wajib diisi",
                    })}
                    placeholder="Nama bisnis atau brand"
                    className="h-12 border-gray-300 bg-gray-50 dark:bg-gray-900 dark:border-gray-700"
                  />
                  {errors.businessName && (
                    <p className="text-sm text-[#D32F2F] flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.businessName.message}
                    </p>
                  )}
                </div>

                {/* WhatsApp & Email Grid */}
                <div className="grid gap-6 md:grid-cols-2">
                  {/* WhatsApp */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="whatsapp"
                      className="font-medium text-gray-900 dark:text-white"
                    >
                      Nomor WhatsApp <span className="text-[#D32F2F]">*</span>
                    </Label>
                    <Input
                      id="whatsapp"
                      {...register("whatsapp", {
                        required: "Nomor WhatsApp wajib diisi",
                        pattern: {
                          value: /^[0-9+\s-()]+$/,
                          message: "Format nomor tidak valid",
                        },
                      })}
                      placeholder="Nomor WhatsApp aktif"
                      className="h-12 border-gray-300 bg-gray-50 dark:bg-gray-900 dark:border-gray-700"
                    />
                    {errors.whatsapp && (
                      <p className="text-sm text-[#D32F2F] flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.whatsapp.message}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="font-medium text-gray-900 dark:text-white"
                    >
                      Email <span className="text-[#D32F2F]">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      {...register("email", {
                        required: "Email wajib diisi",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Format email tidak valid",
                        },
                      })}
                      placeholder="Email aktif"
                      className="h-12 border-gray-300 bg-gray-50 dark:bg-gray-900 dark:border-gray-700"
                    />
                    {errors.email && (
                      <p className="text-sm text-[#D32F2F] flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Service (Pre-selected) */}
                <div className="space-y-2">
                  <Label
                    htmlFor="service"
                    className="font-medium text-gray-900 dark:text-white"
                  >
                    Layanan yang Diminati{" "}
                    <span className="text-[#D32F2F]">*</span>
                  </Label>
                  <Select
                    value={selectedService}
                    onValueChange={(value) => setValue("service", value)}
                  >
                    <SelectTrigger className="h-12 border-gray-300 bg-gray-50 dark:bg-gray-900 dark:border-gray-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trial-demo-social-media">
                        Trial Demo Social Media Management – 1 Bulan
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Goal Dropdown */}
                <div className="space-y-2">
                  <Label
                    htmlFor="goal"
                    className="font-medium text-gray-900 dark:text-white"
                  >
                    Tujuan Utama <span className="text-[#D32F2F]">*</span>
                  </Label>
                  <Select
                    value={selectedGoal}
                    onValueChange={(value) =>
                      setValue("goal", value, { shouldValidate: true })
                    }
                  >
                    <SelectTrigger className="h-12 border-gray-300 bg-gray-50 dark:bg-gray-900 dark:border-gray-700">
                      <SelectValue placeholder="Pilih tujuan utama Anda" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="branding">Branding</SelectItem>
                      <SelectItem value="leads">Leads</SelectItem>
                      <SelectItem value="penjualan">Penjualan</SelectItem>
                      <SelectItem value="konsultasi-strategi">
                        Konsultasi Strategi
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.goal && (
                    <p className="text-sm text-[#D32F2F] flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      Pilih tujuan utama
                    </p>
                  )}
                </div>

                {/* Additional Notes */}
                <div className="space-y-2">
                  <Label
                    htmlFor="notes"
                    className="font-medium text-gray-900 dark:text-white"
                  >
                    Catatan Tambahan{" "}
                    <span className="text-sm text-gray-500">(Opsional)</span>
                  </Label>
                  <Textarea
                    id="notes"
                    {...register("notes")}
                    placeholder="Ceritakan sedikit kebutuhan Anda (opsional)"
                    rows={4}
                    className="border-gray-300 bg-gray-50 dark:bg-gray-900 dark:border-gray-700"
                  />
                </div>

                {/* Trust Box */}
                <div className="p-6 border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 rounded-xl">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Trial 1 bulan penuh
                      </span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Akun real (bukan simulasi)
                      </span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Lanjut bulan berikutnya cukup bayar 50%
                      </span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Tanpa kontrak jangka panjang
                      </span>
                    </div>
                  </div>
                </div>
                <TrialPaymentInfo />
                <TrialTerms />
                <TrialBankAccounts />

                {/* Consent Checkbox */}
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="consent"
                    checked={agreedToContact}
                    onCheckedChange={setAgreedToContact}
                    className="mt-1"
                  />
                  <Label
                    htmlFor="consent"
                    className="text-sm leading-relaxed text-gray-700 cursor-pointer dark:text-gray-300"
                  >
                    Saya setuju dihubungi via WhatsApp dan memahami bahwa jika
                    melanjutkan setelah trial, saya membayar Rp{" "}
                    {trialConfig.payAfterTrial.toLocaleString()}.
                  </Label>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={!agreedToContact}
                  className="w-full bg-[#D32F2F] hover:bg-[#B71C1C] text-white h-14 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  🎯 Klaim Trial Demo Sekarang
                </Button>

                {/* Micro Copy */}
                <p className="text-sm italic text-center text-gray-500 dark:text-gray-500">
                  Dengan mengisi formulir, Anda menyetujui skema Trial Gratis 1
                  Bulan dan pembayaran Rp 250.000 jika melanjutkan.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
